const EXPERIMENT_TYPE_LABELS = {
  pilot: "pilot",
  exploratory: "exploratory",
  confirmatory: "confirmatory",
};

const EXPERIMENTAL_UNIT_LABELS = {
  animal: "animal",
  cage: "cage",
  other: "other",
};

const EXPERIMENTAL_DESIGN_LABELS = {
  completely_randomised_single_factor: "completely randomised single factor design",
  randomised_block: "randomised block design",
  completely_randomised_factorial: "completely randomised factorial design",
  randomised_block_factorial: "randomised block factorial design",
  split_plot: "split plot design",
  other: "other",
};

const getValue = (value) => (value && String(value).trim() ? String(value).trim() : "");

const getExperimentalUnitText = (section30 = {}) => {
  if (section30.experimentalUnit === "other") {
    return getValue(section30.experimentalUnitOther) || "other";
  }
  return EXPERIMENTAL_UNIT_LABELS[section30.experimentalUnit] || "";
};

const getMainFactorInfo = (section30 = {}) => {
  if (section30.mainFactorType === "factorial") {
    const factors = getValue(section30.factorialFactors);
    return factors ? { factorial: true, text: factors } : null;
  }
  const factor = getValue(section30.mainFactor);
  // backward compat: old factorialDesign field
  if (!factor && section30.factorialDesign === "yes") {
    const factors = getValue(section30.factorialFactors);
    return factors ? { factorial: true, text: factors } : null;
  }
  return factor ? { factorial: false, text: factor } : null;
};

const getStandardDeviationText = (section30 = {}) => {
  const value = getValue(section30.standardDeviationValue);
  const notes = getValue(section30.standardDeviationNotes);
  switch (section30.standardDeviationSource) {
    case "known":
      return value || notes;
    case "unknown_first_time":
      return "unknown — cannot be estimated (first time performing this experiment)";
    case "unknown_pilot":
      return "unknown — will be estimated in a pilot experiment";
    case "other":
      return notes || value;
    default:
      return value || notes;
  }
};

const getExperimentalDesignText = (section30 = {}) => {
  if (section30.experimentalDesignType === "other") {
    return getValue(section30.experimentalDesignOther) || "other";
  }
  return EXPERIMENTAL_DESIGN_LABELS[section30.experimentalDesignType] || "";
};

const getBiasMeasuresText = (section30 = {}) => {
  const parts = [];
  if (section30.biasRandomisation) parts.push("randomisation of animals between groups");
  if (section30.biasBlinding)
    parts.push("blinding of experimenters during procedures, measures, and data analysis");
  if (section30.biasOrderRandomised)
    parts.push("randomised order of treatment and measures between groups");
  if (section30.biasInclusionExclusion) {
    const text = getValue(section30.biasInclusionExclusionText);
    parts.push(
      text
        ? `inclusion and exclusion criteria set in advance: ${text}`
        : "inclusion and exclusion criteria set in advance"
    );
  }
  if (section30.biasOther) {
    const text = getValue(section30.biasOtherText);
    if (text) parts.push(text);
  }
  // backward compat: old free-text field
  if (parts.length === 0) {
    const old = getValue(section30.biasMeasures);
    if (old) return old;
  }
  return parts.join("; ");
};

const getSampleSizeText = (section30 = {}) => {
  const method = section30.sampleSizeMethod;

  if (method === "apriori_power_analysis") {
    const label = "an a-priori power analysis";
    const details = [];
    if (getValue(section30.ssEffectSize)) details.push(`effect size: ${section30.ssEffectSize}`);
    if (getValue(section30.ssSd)) details.push(`standard deviation: ${section30.ssSd}`);
    if (getValue(section30.ssAlpha)) details.push(`significance level (α): ${section30.ssAlpha}`);
    if (getValue(section30.ssPower)) details.push(`power: ${section30.ssPower}`);
    if (getValue(section30.ssAlternativeHypothesis))
      details.push(`alternative hypothesis: ${section30.ssAlternativeHypothesis}`);
    if (getValue(section30.ssTailed)) details.push(`${section30.ssTailed} test`);
    if (getValue(section30.ssAttrition)) details.push(`attrition: ${section30.ssAttrition}`);
    if (getValue(section30.ssSampleSize))
      details.push(`sample size per group: ${section30.ssSampleSize}`);
    return details.length > 0 ? `${label} (${details.join(", ")})` : label;
  }

  if (method === "cohens_d") {
    const label = "the Cohen's d method";
    const details = [];
    if (getValue(section30.ssEffectSize)) details.push(`effect size (ES): ${section30.ssEffectSize}`);
    if (getValue(section30.ssSd)) details.push(`standard deviation (SD): ${section30.ssSd}`);
    if (getValue(section30.ssTailed)) details.push(`${section30.ssTailed} test`);
    const ses =
      section30.ssEffectSize && section30.ssSd && parseFloat(section30.ssSd) !== 0
        ? (parseFloat(section30.ssEffectSize) / parseFloat(section30.ssSd)).toFixed(3)
        : getValue(section30.ssSES);
    if (ses) details.push(`SES (ES/SD): ${ses}`);
    if (getValue(section30.ssExpectedEffect))
      details.push(`expected effect: ${section30.ssExpectedEffect}`);
    if (getValue(section30.ssAttrition)) details.push(`attrition: ${section30.ssAttrition}`);
    if (getValue(section30.ssSampleSize))
      details.push(`sample size per group: ${section30.ssSampleSize}`);
    return details.length > 0 ? `${label} (${details.join(", ")})` : label;
  }

  if (method === "resource_equation") {
    const label = "the resource equation method";
    const details = [];
    if (getValue(section30.ssEValue)) details.push(`E value: ${section30.ssEValue}`);
    if (getValue(section30.ssAttrition)) details.push(`attrition: ${section30.ssAttrition}`);
    if (getValue(section30.ssSampleSize)) details.push(`sample size: ${section30.ssSampleSize}`);
    return details.length > 0 ? `${label} (${details.join(", ")})` : label;
  }

  if (method === "other") {
    return getValue(section30.sampleSizeDetails) || "another method";
  }

  // backward compat: old free-text field
  return getValue(section30.sampleSizeDetails);
};

export const buildSection30Summary = (exp, index) => {
  const section30 = exp.section30 || {};
  const lines = [];

  const experimentType = EXPERIMENT_TYPE_LABELS[section30.experimentType];
  const experimentalUnit = getExperimentalUnitText(section30);
  const mainFactorInfo = getMainFactorInfo(section30);
  const outcomeMeasure = getValue(section30.outcomeMeasure);
  const hypothesis = getValue(section30.hypothesis);
  const standardDeviation = getStandardDeviationText(section30);
  const experimentalDesign = getExperimentalDesignText(section30);
  const biasMeasures = getBiasMeasuresText(section30);
  const sampleSizeMethod = getSampleSizeText(section30);
  const analysisTest = getValue(section30.analysisTest);

  if (experimentType) lines.push(`1. This is a ${experimentType} experiment.`);
  if (experimentalUnit) lines.push(`2. The experimental unit is the ${experimentalUnit}.`);
  if (mainFactorInfo) {
    if (mainFactorInfo.factorial) {
      lines.push(
        `3. This experiment is based on a factorial design. The factors to be studied are: ${mainFactorInfo.text}.`
      );
    } else {
      lines.push(`3. The main factor is: ${mainFactorInfo.text}.`);
    }
  }
  if (outcomeMeasure) lines.push(`4. The outcome to measure is: ${outcomeMeasure}.`);
  if (hypothesis) lines.push(`5. The hypothesis is: ${hypothesis}.`);
  if (standardDeviation) lines.push(`6. The standard deviation is: ${standardDeviation}.`);
  if (experimentalDesign) lines.push(`7. The experimental design is: ${experimentalDesign}.`);
  if (biasMeasures) lines.push(`8. The measures to avoid biases are: ${biasMeasures}.`);
  if (sampleSizeMethod) lines.push(`9. The method to calculate sample size is: ${sampleSizeMethod}.`);
  if (analysisTest) lines.push(`10. This test will be used to analyse the results: ${analysisTest}.`);

  return {
    experimentLabel: exp.experimentObjective || `Experiment ${index + 1}`,
    lines,
    hasContent: lines.length > 0,
  };
};
